import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "item", "input", "badge"]

  connect() {
    this.draggedItem = null
    this.placeholder = null
    this.previousItemStyle = null
    this.dragOffsetX = 0
    this.dragOffsetY = 0
    this.boundMove = this.moveDrag.bind(this)
    this.boundEnd = this.endDrag.bind(this)
    this.updateOrder()
  }

  disconnect() {
    this.removeDragListeners()
    this.resetDraggedItem()
    this.removePlaceholder()
  }

  startDrag(event) {
    if (event.button !== undefined && event.button !== 0) return

    const item = event.currentTarget.closest("[data-gallery-sort-target='item']")
    if (!item) return

    event.preventDefault()

    this.draggedItem = item
    const box = item.getBoundingClientRect()
    this.dragOffsetX = event.clientX - box.left
    this.dragOffsetY = event.clientY - box.top
    this.previousItemStyle = item.getAttribute("style")
    this.placeholder = this.createPlaceholder(item, box)
    this.listTarget.insertBefore(this.placeholder, item.nextSibling)

    this.positionDraggedItem(event.clientX, event.clientY, box)
    item.classList.add("is-dragging")
    this.element.classList.add("is-dragging")

    if (event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
      event.currentTarget.setPointerCapture(event.pointerId)
    }

    document.addEventListener("pointermove", this.boundMove)
    document.addEventListener("pointerup", this.boundEnd)
    document.addEventListener("pointercancel", this.boundEnd)
  }

  moveDrag(event) {
    if (!this.draggedItem) return

    event.preventDefault()
    this.positionDraggedItem(event.clientX, event.clientY)

    const nextItem = this.itemAfterPointer(event.clientX, event.clientY)
    if (nextItem) {
      this.listTarget.insertBefore(this.placeholder, nextItem)
    } else {
      this.listTarget.appendChild(this.placeholder)
    }
  }

  endDrag() {
    if (!this.draggedItem) return

    if (this.placeholder?.parentNode) {
      this.placeholder.parentNode.insertBefore(this.draggedItem, this.placeholder)
    }

    this.resetDraggedItem()
    this.removePlaceholder()
    this.element.classList.remove("is-dragging")
    this.updateOrder()
    this.removeDragListeners()
  }

  updateOrder() {
    this.orderedItems.forEach((item, index) => {
      const input = item.querySelector("[data-gallery-sort-target='input']")
      const badge = item.querySelector("[data-gallery-sort-target='badge']")

      if (input) input.value = item.dataset.attachmentId
      if (badge) badge.textContent = `#${index + 1}`
    })
  }

  itemAfterPointer(pointerX, pointerY) {
    const availableItems = this.orderedItems.filter((item) => item !== this.draggedItem)

    return availableItems.find((item) => {
      const box = item.getBoundingClientRect()
      const middleY = box.top + box.height / 2
      const middleX = box.left + box.width / 2
      const sameRow = pointerY >= box.top && pointerY <= box.bottom

      return pointerY < middleY || (sameRow && pointerX < middleX)
    })
  }

  createPlaceholder(item, box) {
    const placeholder = document.createElement("div")
    placeholder.className = item.className
    placeholder.classList.remove("is-dragging")
    placeholder.classList.add("company-album-sort-placeholder")
    placeholder.removeAttribute("data-gallery-sort-target")
    placeholder.removeAttribute("data-attachment-id")
    placeholder.setAttribute("aria-hidden", "true")
    placeholder.innerHTML = '<div class="company-album-sort-placeholder__inner"></div>'
    placeholder.querySelector(".company-album-sort-placeholder__inner").style.height = `${box.height}px`

    return placeholder
  }

  positionDraggedItem(clientX, clientY, box = this.draggedItem.getBoundingClientRect()) {
    Object.assign(this.draggedItem.style, {
      left: `${clientX - this.dragOffsetX}px`,
      margin: "0",
      position: "fixed",
      top: `${clientY - this.dragOffsetY}px`,
      width: `${box.width}px`,
      zIndex: "1080"
    })
  }

  resetDraggedItem() {
    if (!this.draggedItem) return

    this.draggedItem.classList.remove("is-dragging")

    if (this.previousItemStyle === null) {
      this.draggedItem.removeAttribute("style")
    } else {
      this.draggedItem.setAttribute("style", this.previousItemStyle)
    }

    this.draggedItem = null
    this.previousItemStyle = null
  }

  removePlaceholder() {
    this.placeholder?.remove()
    this.placeholder = null
  }

  removeDragListeners() {
    this.element.classList.remove("is-dragging")
    document.removeEventListener("pointermove", this.boundMove)
    document.removeEventListener("pointerup", this.boundEnd)
    document.removeEventListener("pointercancel", this.boundEnd)
  }

  get orderedItems() {
    return Array.from(this.listTarget.querySelectorAll("[data-gallery-sort-target='item']"))
  }
}
